import React, { useState, useEffect } from 'react';
import TaskCard from './TaskCard';
import TaskStats from './TaskStats';
import Timeline from '../admin/Timeline';
import apiService from '../../services/api';
import useTokenRefresh from '../../hooks/useTokenRefresh';

const TaskBoard = ({ user, selectedCountry = 'all' }) => {
  useTokenRefresh(); // Auto-refresh tokens
  const [tasks, setTasks] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filter, setFilter] = useState('all');
  const [uploadStatus, setUploadStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [tasksPerPage] = useState(20);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [viewMode, setViewMode] = useState('board'); // 'list' or 'board'
  const [selectedTaskForTimeline, setSelectedTaskForTimeline] = useState(null);

  useEffect(() => {
    loadTasks();
  }, [selectedCountry]);

  const loadTasks = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedCountry && selectedCountry !== 'all') {
        params.append('country_id', selectedCountry);
      }
      const queryString = params.toString();
      const response = await apiService.get(`/tasks${queryString ? `?${queryString}` : ''}`);

      // Handle different response formats
      let tasksData = [];
      if (Array.isArray(response)) {
        tasksData = response;
      } else if (response && Array.isArray(response.data)) {
        tasksData = response.data;
      } else if (response && response.tasks && Array.isArray(response.tasks)) {
        tasksData = response.tasks;
      }

      setTasks(tasksData);
    } catch (error) {
      console.error('Failed to load tasks:', error);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      const allowedTypes = ['.csv', '.xlsx', '.xls'];
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();

      if (allowedTypes.includes(fileExtension)) {
        setSelectedFile(file);
      } else {
        alert('Please select a CSV or Excel file');
        event.target.value = '';
      }
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setUploadStatus('Please select a file first');
      setTimeout(() => setUploadStatus(''), 3000);
      return;
    }

    try {
      setUploading(true);
      setUploadStatus('Uploading and processing file...');

      const formData = new FormData();
      formData.append('file', selectedFile);
      if (selectedCountry && selectedCountry !== 'all') {
        formData.append('country_id', selectedCountry);
      }

      const response = await apiService.post('/tasks/upload', formData);

      let message = 'Upload completed!';
      if (response.created > 0) {
        message += ` Created ${response.created} new tasks.`;
      }
      if (response.duplicates > 0) {
        message += ` Skipped ${response.duplicates} duplicate tasks.`;
      }

      setUploadStatus(message);
      setSelectedFile(null);
      document.getElementById('file-input').value = '';
      loadTasks();

      setTimeout(() => setUploadStatus(''), 5000);
    } catch (error) {
      setUploadStatus('Upload failed. Please try again.');
      setTimeout(() => setUploadStatus(''), 3000);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await apiService.deleteTask(taskId);
      loadTasks();
    } catch (error) {
      console.error('Failed to delete task:', error);
    }
  };

  const handleShowTimeline = (task) => {
    setSelectedTaskForTimeline(task);
  };

  const handleCloseTimeline = () => {
    setSelectedTaskForTimeline(null);
  };

  const updateTaskStatus = async (taskId, status) => {
    try {
      await apiService.updateTaskStatus(taskId, status);
      loadTasks();
    } catch (error) {
      console.error('Failed to update task status:', error);
    }
  };

  // Drag and drop handlers
  const [draggedOver, setDraggedOver] = useState(null);

  const handleDragStart = (e, task) => {
    e.dataTransfer.setData('text/plain', JSON.stringify(task));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e, status) => {
    e.preventDefault();
    setDraggedOver(status);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDraggedOver(null);
  };

  const handleDrop = async (e, targetStatus) => {
    e.preventDefault();
    setDraggedOver(null);
    try {
      const taskData = JSON.parse(e.dataTransfer.getData('text/plain'));
      if (taskData.status !== targetStatus) {
        await updateTaskStatus(taskData.id, targetStatus);
      }
    } catch (error) {
      console.error('Failed to handle drop:', error);
    }
  };

  const getTasksWithOverdue = () => {
    return tasks.map(task => {
      if (task.status !== 'registered') {
        const dueDate = task.due_date ? new Date(task.due_date) : new Date(task.created_at);
        const daysDiff = (Date.now() - dueDate.getTime()) / (1000 * 60 * 60 * 24);
        if (daysDiff >= 7) {
          return { ...task, status: 'overdue' };
        }
      }
      return task;
    });
  };

  const getFilteredTasks = () => {
    const tasksWithOverdue = getTasksWithOverdue();
    let filtered = tasksWithOverdue;

    // Apply status filter
    if (filter !== 'all') {
      filtered = filtered.filter(task => task.status === filter);
    }

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(task =>
        task.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        task.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        task.assignee_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        task.assignee_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aVal = a[sortBy] || '';
      let bVal = b[sortBy] || '';

      if (sortBy === 'created_at' || sortBy === 'due_date') {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }

      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    return filtered;
  };

  const getPaginatedTasks = () => {
    const filtered = getFilteredTasks();
    const startIndex = (currentPage - 1) * tasksPerPage;
    return filtered.slice(startIndex, startIndex + tasksPerPage);
  };

  const getTotalPages = () => {
    return Math.ceil(getFilteredTasks().length / tasksPerPage);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      pending: { bg: '#fff3cd', color: '#856404', text: 'Pending' },
      in_progress: { bg: '#cce5ff', color: '#004085', text: 'In Progress' },
      registered: { bg: '#d4edda', color: '#155724', text: 'Completed' },
      overdue: { bg: '#f8d7da', color: '#721c24', text: 'Overdue' }
    };

    const style = statusColors[status] || statusColors.pending;
    return (
      <span style={{
        padding: '4px 8px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '500',
        backgroundColor: style.bg,
        color: style.color
      }}>
        {style.text}
      </span>
    );
  };

  const taskColumns = [
    { status: 'pending', title: 'Pending', color: '#f57c00' },
    { status: 'in_progress', title: 'In Progress', color: '#1976d2' },
    { status: 'registered', title: 'Registered', color: '#388e3c' },
    { status: 'overdue', title: 'Overdue', color: '#d32f2f' }
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '24px'
    }}>
      <div style={{ marginBottom: '32px' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px'
        }}>
          <h1 style={{
            margin: 0,
            fontSize: '32px',
            fontWeight: '700',
            color: 'white',
            textShadow: '0 2px 4px rgba(0,0,0,0.3)'
          }}>
            📋 Task Allocation Board
          </h1>

          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <button
              onClick={() => setViewMode(viewMode === 'list' ? 'board' : 'list')}
              style={{
                padding: '10px 16px',
                background: 'rgba(255,255,255,0.2)',
                border: '1px solid rgba(255,255,255,0.3)',
                borderRadius: '8px',
                color: 'white',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                backdropFilter: 'blur(10px)',
                transition: 'all 0.3s ease'
              }}
              onMouseOver={(e) => e.target.style.background = 'rgba(255,255,255,0.3)'}
              onMouseOut={(e) => e.target.style.background = 'rgba(255,255,255,0.2)'}
            >
              {viewMode === 'list' ? '📊 Board View' : '📋 List View'}
            </button>

            <button
              onClick={loadTasks}
              style={{
                padding: '10px 16px',
                background: 'rgba(255,255,255,0.2)',
                border: '1px solid rgba(255,255,255,0.3)',
                borderRadius: '8px',
                color: 'white',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                backdropFilter: 'blur(10px)',
                transition: 'all 0.3s ease'
              }}
              onMouseOver={(e) => e.target.style.background = 'rgba(255,255,255,0.3)'}
              onMouseOut={(e) => e.target.style.background = 'rgba(255,255,255,0.2)'}
            >
              🔄 Refresh
            </button>
          </div>
        </div>

        {user?.role === 'super_admin' && (
          <div style={{
            background: 'rgba(255,255,255,0.95)',
            padding: '28px',
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
            border: '1px solid rgba(255,255,255,0.2)',
            marginBottom: '32px',
            backdropFilter: 'blur(10px)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
              <div style={{
                width: '48px',
                height: '48px',
                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px'
              }}>
                📤
              </div>
              <div>
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#2d3748' }}>
                  Upload Task Allocation File
                </h2>
                <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#718096' }}>
                  Bulk import tasks from CSV or Excel files
                </p>
              </div>
            </div>

            <p style={{ fontSize: '14px', color: '#666', marginBottom: '16px' }}>
              Upload CSV or Excel file with any columns. Last column should be Assignee Email.<br />
              <strong>Note:</strong> Duplicate tasks (same details + assignee) will be automatically skipped.
            </p>

            <div style={{
              position: 'relative',
              marginBottom: '20px'
            }}>
              <input
                id="file-input"
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileSelect}
                style={{
                  position: 'absolute',
                  opacity: 0,
                  width: '100%',
                  height: '100%',
                  cursor: 'pointer'
                }}
              />
              <div style={{
                padding: '24px',
                border: '2px dashed #cbd5e0',
                borderRadius: '12px',
                textAlign: 'center',
                background: selectedFile ? '#f0fff4' : '#f7fafc',
                transition: 'all 0.3s ease',
                cursor: 'pointer'
              }}>
                <div style={{ fontSize: '32px', marginBottom: '12px' }}>
                  {selectedFile ? '✅' : '📁'}
                </div>
                <p style={{
                  margin: 0,
                  fontSize: '16px',
                  fontWeight: '500',
                  color: selectedFile ? '#38a169' : '#4a5568'
                }}>
                  {selectedFile ? selectedFile.name : 'Click to select or drag & drop your file'}
                </p>
                <p style={{
                  margin: '8px 0 0 0',
                  fontSize: '14px',
                  color: '#718096'
                }}>
                  Supports CSV, XLSX, XLS files
                </p>
              </div>
            </div>



            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <button
                onClick={handleUpload}
                disabled={!selectedFile || uploading}
                style={{
                  background: selectedFile && !uploading
                    ? 'linear-gradient(135deg, #667eea, #764ba2)'
                    : '#e2e8f0',
                  color: selectedFile && !uploading ? 'white' : '#a0aec0',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: selectedFile && !uploading ? 'pointer' : 'not-allowed',
                  fontSize: '14px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.3s ease',
                  boxShadow: selectedFile && !uploading ? '0 4px 12px rgba(102, 126, 234, 0.4)' : 'none'
                }}
              >
                {uploading && (
                  <div style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid transparent',
                    borderTop: '2px solid white',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                )}
                {uploading ? '⏳ Processing...' : '🚀 Upload Tasks'}
              </button>

              {selectedFile && (
                <button
                  onClick={() => {
                    setSelectedFile(null);
                    document.getElementById('file-input').value = '';
                  }}
                  style={{
                    background: 'transparent',
                    color: '#e53e3e',
                    border: '1px solid #e53e3e',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'all 0.3s ease'
                  }}
                >
                  ❌ Clear
                </button>
              )}
            </div>

            {uploadStatus && (
              <div style={{
                marginTop: '12px',
                padding: '12px',
                borderRadius: '6px',
                fontSize: '14px',
                background: uploadStatus.includes('failed') || uploadStatus.includes('Please select')
                  ? '#ffebee' : '#e8f5e8',
                color: uploadStatus.includes('failed') || uploadStatus.includes('Please select')
                  ? '#c62828' : '#2e7d32',
                border: `1px solid ${uploadStatus.includes('failed') || uploadStatus.includes('Please select')
                  ? '#ffcdd2' : '#c8e6c9'}`
              }}>
                {uploadStatus}
              </div>
            )}

            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
              
              @keyframes fadeIn {
                from { opacity: 0; transform: translateY(20px); }
                to { opacity: 1; transform: translateY(0); }
              }
              
              @keyframes slideIn {
                from { transform: translateX(-100%); opacity: 0; }
                to { transform: translateX(0); opacity: 1; }
              }
              
              @keyframes pulse {
                0%, 100% { transform: scale(1); }
                50% { transform: scale(1.05); }
              }
              
              .task-row:hover {
                animation: fadeIn 0.3s ease;
              }
              
              .filter-tag {
                animation: slideIn 0.3s ease;
              }
              
              .upload-area:hover {
                animation: pulse 2s infinite;
              }
            `}</style>
          </div>
        )}
      </div>

      <TaskStats tasks={tasks} />

      {/* Enhanced Controls Bar */}
      <div style={{
        background: 'rgba(255,255,255,0.95)',
        padding: '24px',
        borderRadius: '16px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        marginBottom: '32px',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255,255,255,0.2)'
      }}>
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '20px',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '16px'
        }}>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Enhanced Search */}
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                placeholder="🔍 Search tasks, assignees..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                style={{
                  padding: '12px 16px 12px 40px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '12px',
                  fontSize: '14px',
                  minWidth: '280px',
                  background: 'white',
                  transition: 'all 0.3s ease',
                  outline: 'none'
                }}
                onFocus={(e) => e.target.style.borderColor = '#667eea'}
                onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
              />
              <div style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                fontSize: '16px',
                color: '#a0aec0'
              }}>
                🔍
              </div>
            </div>

            {/* Enhanced Status Filter */}
            <select
              value={filter}
              onChange={(e) => {
                setFilter(e.target.value);
                setCurrentPage(1);
              }}
              style={{
                padding: '12px 16px',
                border: '2px solid #e2e8f0',
                borderRadius: '12px',
                fontSize: '14px',
                minWidth: '160px',
                background: 'white',
                cursor: 'pointer',
                outline: 'none',
                transition: 'all 0.3s ease'
              }}
              onFocus={(e) => e.target.style.borderColor = '#667eea'}
              onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
            >
              <option value="all">📊 All Status</option>
              <option value="pending">⏳ Pending</option>
              <option value="in_progress">🔄 In Progress</option>
              <option value="registered">✅ Completed</option>
              <option value="overdue">⚠️ Overdue</option>
            </select>

            {/* Enhanced Sort Controls */}
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                style={{
                  padding: '12px 16px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '12px',
                  fontSize: '14px',
                  background: 'white',
                  cursor: 'pointer',
                  outline: 'none'
                }}
              >
                <option value="created_at">📅 Created Date</option>
                <option value="due_date">⏰ Due Date</option>
                <option value="title">📝 Title</option>
                <option value="status">📊 Status</option>
              </select>

              <button
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                style={{
                  padding: '12px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '12px',
                  background: 'white',
                  cursor: 'pointer',
                  fontSize: '16px',
                  transition: 'all 0.3s ease'
                }}
                title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
              >
                {sortOrder === 'asc' ? '⬆️' : '⬇️'}
              </button>
            </div>
          </div>

          {/* Filter Tags & Clear */}
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
            {searchTerm && (
              <div style={{
                padding: '6px 12px',
                background: '#e6fffa',
                border: '1px solid #81e6d9',
                borderRadius: '20px',
                fontSize: '12px',
                color: '#234e52',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                🔍 "{searchTerm}"
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setCurrentPage(1);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '12px',
                    color: '#234e52'
                  }}
                >
                  ❌
                </button>
              </div>
            )}

            {filter !== 'all' && (
              <div style={{
                padding: '6px 12px',
                background: '#fef5e7',
                border: '1px solid #f6ad55',
                borderRadius: '20px',
                fontSize: '12px',
                color: '#744210',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                📊 {filter.replace('_', ' ')}
                <button
                  onClick={() => {
                    setFilter('all');
                    setCurrentPage(1);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '12px',
                    color: '#744210'
                  }}
                >
                  ❌
                </button>
              </div>
            )}

            {(searchTerm || filter !== 'all') && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setFilter('all');
                  setCurrentPage(1);
                }}
                style={{
                  padding: '8px 16px',
                  border: '2px solid #e53e3e',
                  borderRadius: '12px',
                  background: 'white',
                  color: '#e53e3e',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.3s ease'
                }}
                onMouseOver={(e) => {
                  e.target.style.background = '#e53e3e';
                  e.target.style.color = 'white';
                }}
                onMouseOut={(e) => {
                  e.target.style.background = 'white';
                  e.target.style.color = '#e53e3e';
                }}
              >
                🗑️ Clear All Filters
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Timeline Panel */}
      {selectedTaskForTimeline && (
        <div style={{
          background: 'rgba(255,255,255,0.95)',
          borderRadius: '16px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          marginBottom: '32px',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255,255,255,0.2)',
          overflow: 'hidden'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '20px 24px',
            background: 'linear-gradient(135deg, #667eea, #764ba2)',
            color: 'white'
          }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700' }}>
                📋 Timeline for: {selectedTaskForTimeline.task_details?.name || selectedTaskForTimeline.name || 'Untitled Task'}
              </h3>
              <p style={{ margin: '4px 0 0 0', fontSize: '14px', opacity: 0.9 }}>
                Task ID: #{selectedTaskForTimeline.id}
              </p>
            </div>
            <button
              onClick={handleCloseTimeline}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: '1px solid rgba(255,255,255,0.3)',
                borderRadius: '8px',
                color: 'white',
                cursor: 'pointer',
                padding: '8px 12px',
                fontSize: '16px',
                fontWeight: '600',
                transition: 'all 0.3s ease'
              }}
              onMouseOver={(e) => {
                e.target.style.background = 'rgba(255,255,255,0.3)';
              }}
              onMouseOut={(e) => {
                e.target.style.background = 'rgba(255,255,255,0.2)';
              }}
            >
              ✕ Close
            </button>
          </div>
          <div style={{ padding: '24px' }}>
            <Timeline entityType="task" entityId={selectedTaskForTimeline.id} />
          </div>
        </div>
      )}

      {/* Enhanced Task Views */}
      {viewMode === 'list' ? (
        <div style={{
          background: 'rgba(255,255,255,0.95)',
          borderRadius: '16px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          overflow: 'hidden',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255,255,255,0.2)'
        }}>
          {/* Enhanced Table Header */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 120px',
            gap: '20px',
            padding: '20px 24px',
            background: 'linear-gradient(135deg, #667eea, #764ba2)',
            color: 'white',
            fontSize: '14px',
            fontWeight: '700',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            <div>📋 Task Details</div>
            <div>👤 Assignee</div>
            <div>📊 Status</div>
            <div>📅 Created</div>
            <div>⏰ Due Date</div>
            <div>⚙️ Actions</div>
          </div>

          {/* Task Rows */}
          {getPaginatedTasks().length > 0 ? (
            getPaginatedTasks().map((task, index) => (
              <div
                key={task.id || index}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 120px',
                  gap: '20px',
                  padding: '20px 24px',
                  borderBottom: '1px solid rgba(0,0,0,0.05)',
                  alignItems: 'center',
                  fontSize: '14px',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
                onMouseOver={(e) => e.currentTarget.style.background = 'rgba(102, 126, 234, 0.05)'}
                onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <div>
                  <div style={{
                    fontWeight: '600',
                    marginBottom: '6px',
                    color: '#2d3748',
                    fontSize: '15px'
                  }}>
                    {task.title || '📝 Untitled Task'}
                  </div>
                  {task.description && (
                    <div style={{
                      color: '#718096',
                      fontSize: '13px',
                      lineHeight: '1.4'
                    }}>
                      {task.description.length > 80
                        ? task.description.substring(0, 80) + '...'
                        : task.description}
                    </div>
                  )}
                </div>

                <div>
                  <div style={{
                    fontWeight: '600',
                    color: '#2d3748',
                    marginBottom: '4px'
                  }}>
                    {task.assignee_name || '👤 Unassigned'}
                  </div>
                  <div style={{
                    color: '#718096',
                    fontSize: '12px',
                    fontFamily: 'monospace'
                  }}>
                    {task.assignee_email}
                  </div>
                </div>

                <div>
                  {getStatusBadge(task.status)}
                </div>

                <div style={{ color: '#666' }}>
                  {formatDate(task.created_at)}
                </div>

                <div style={{ color: '#666' }}>
                  {formatDate(task.due_date)}
                </div>

                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <select
                    value={task.status}
                    onChange={(e) => updateTaskStatus(task.id, e.target.value)}
                    style={{
                      padding: '6px 10px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '12px',
                      background: 'white',
                      cursor: 'pointer',
                      outline: 'none',
                      fontWeight: '500'
                    }}
                  >
                    <option value="pending">⏳ Pending</option>
                    <option value="in_progress">🔄 In Progress</option>
                    <option value="registered">✅ Completed</option>
                  </select>

                  {user?.role === 'super_admin' && (
                    <button
                      onClick={() => {
                        if (window.confirm('Are you sure you want to delete this task?')) {
                          handleDeleteTask(task.id);
                        }
                      }}
                      style={{
                        padding: '6px 8px',
                        border: 'none',
                        background: 'linear-gradient(135deg, #e53e3e, #c53030)',
                        color: 'white',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        transition: 'all 0.3s ease',
                        boxShadow: '0 2px 8px rgba(229, 62, 62, 0.3)'
                      }}
                      title="Delete task"
                      onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
                      onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div style={{
              padding: '60px 20px',
              textAlign: 'center',
              color: '#718096'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>
                {searchTerm || filter !== 'all' ? '🔍' : '📋'}
              </div>
              <h3 style={{
                margin: '0 0 8px 0',
                fontSize: '18px',
                fontWeight: '600',
                color: '#4a5568'
              }}>
                {searchTerm || filter !== 'all'
                  ? 'No tasks match your filters'
                  : 'No tasks available'}
              </h3>
              <p style={{ margin: 0, fontSize: '14px' }}>
                {searchTerm || filter !== 'all'
                  ? 'Try adjusting your search or filter criteria'
                  : 'Upload a file or create tasks to get started'}
              </p>
            </div>
          )}

          {/* Enhanced Pagination */}
          {getTotalPages() > 1 && (
            <div style={{
              padding: '20px 24px',
              borderTop: '1px solid rgba(0,0,0,0.05)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: 'rgba(102, 126, 234, 0.02)'
            }}>
              <div style={{
                fontSize: '14px',
                color: '#718096',
                fontWeight: '500'
              }}>
                📊 Showing {((currentPage - 1) * tasksPerPage) + 1} to {Math.min(currentPage * tasksPerPage, getFilteredTasks().length)} of {getFilteredTasks().length} tasks
              </div>

              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  style={{
                    padding: '10px 16px',
                    border: '2px solid #e2e8f0',
                    background: currentPage === 1 ? '#f7fafc' : 'white',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: currentPage === 1 ? '#a0aec0' : '#4a5568',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseOver={(e) => {
                    if (currentPage !== 1) {
                      e.target.style.background = '#667eea';
                      e.target.style.color = 'white';
                      e.target.style.borderColor = '#667eea';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (currentPage !== 1) {
                      e.target.style.background = 'white';
                      e.target.style.color = '#4a5568';
                      e.target.style.borderColor = '#e2e8f0';
                    }
                  }}
                >
                  ⬅️ Previous
                </button>

                <div style={{
                  padding: '10px 16px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#4a5568',
                  background: 'rgba(102, 126, 234, 0.1)',
                  borderRadius: '12px',
                  border: '2px solid rgba(102, 126, 234, 0.2)'
                }}>
                  📄 Page {currentPage} of {getTotalPages()}
                </div>

                <button
                  onClick={() => setCurrentPage(Math.min(getTotalPages(), currentPage + 1))}
                  disabled={currentPage === getTotalPages()}
                  style={{
                    padding: '10px 16px',
                    border: '2px solid #e2e8f0',
                    background: currentPage === getTotalPages() ? '#f7fafc' : 'white',
                    cursor: currentPage === getTotalPages() ? 'not-allowed' : 'pointer',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: currentPage === getTotalPages() ? '#a0aec0' : '#4a5568',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseOver={(e) => {
                    if (currentPage !== getTotalPages()) {
                      e.target.style.background = '#667eea';
                      e.target.style.color = 'white';
                      e.target.style.borderColor = '#667eea';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (currentPage !== getTotalPages()) {
                      e.target.style.background = 'white';
                      e.target.style.color = '#4a5568';
                      e.target.style.borderColor = '#e2e8f0';
                    }
                  }}
                >
                  Next ➡️
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        /* Enhanced Board View */
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: '24px'
        }}>
          {taskColumns.map(column => {
            const columnTasks = getFilteredTasks().filter(task => task.status === column.status);
            return (
              <div key={column.status} style={{
                background: 'rgba(255,255,255,0.95)',
                borderRadius: '16px',
                boxShadow: draggedOver === column.status
                  ? '0 12px 40px rgba(102, 126, 234, 0.3)'
                  : '0 8px 32px rgba(0,0,0,0.1)',
                overflow: 'hidden',
                backdropFilter: 'blur(10px)',
                border: draggedOver === column.status
                  ? '2px solid #667eea'
                  : '1px solid rgba(255,255,255,0.2)',
                transition: 'all 0.3s ease',
                transform: draggedOver === column.status ? 'scale(1.02)' : 'scale(1)'
              }}>
                <div style={{
                  padding: '20px',
                  background: `linear-gradient(135deg, ${column.color}, ${column.color}dd)`,
                  color: 'white',
                  fontWeight: '700',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  textShadow: '0 1px 2px rgba(0,0,0,0.2)'
                }}>
                  <span style={{ fontSize: '16px' }}>{column.title}</span>
                  <span style={{
                    background: 'rgba(255,255,255,0.25)',
                    padding: '6px 12px',
                    borderRadius: '20px',
                    fontSize: '13px',
                    fontWeight: '700',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.3)'
                  }}>
                    {columnTasks.length}
                  </span>
                </div>

                <div
                  style={{
                    padding: '20px',
                    maxHeight: '650px',
                    overflowY: 'auto',
                    background: draggedOver === column.status
                      ? 'linear-gradient(to bottom, rgba(102, 126, 234, 0.1), rgba(102, 126, 234, 0.05))'
                      : 'linear-gradient(to bottom, rgba(255,255,255,0.1), transparent)',
                    minHeight: '200px',
                    transition: 'all 0.3s ease'
                  }}
                  onDragOver={handleDragOver}
                  onDragEnter={(e) => handleDragEnter(e, column.status)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, column.status)}
                >
                  {columnTasks.slice(0, 10).map((task, index) => (
                    <div
                      key={task.id || index}
                      draggable
                      onDragStart={(e) => handleDragStart(e, task)}
                      style={{ cursor: 'grab' }}
                      onMouseDown={(e) => e.currentTarget.style.cursor = 'grabbing'}
                      onMouseUp={(e) => e.currentTarget.style.cursor = 'grab'}
                    >
                      <TaskCard
                        task={task}
                        onDelete={handleDeleteTask}
                        onStatusChange={updateTaskStatus}
                        onRefresh={loadTasks}
                        onShowTimeline={handleShowTimeline}
                        user={user}
                      />
                    </div>
                  ))}

                  {columnTasks.length > 10 && (
                    <div style={{
                      textAlign: 'center',
                      padding: '16px',
                      color: '#718096',
                      fontSize: '14px',
                      fontWeight: '500',
                      background: 'rgba(102, 126, 234, 0.05)',
                      borderRadius: '12px',
                      border: '1px dashed rgba(102, 126, 234, 0.2)'
                    }}>
                      📊 +{columnTasks.length - 10} more tasks...
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TaskBoard;